import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  Renderer2,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

export type PaneOptions = {
  /** A number between 0 and 1 representing the minimum size of the pane. Ex: 0.3 for 30% */
  minWidth?: number;
  /** A number between 0 and 1 representing the maximum size of the pane. Ex: 0.7 for 70% */
  maxWidth?: number;
  /** A number between 0 and 1 representing the maximum size of the pane. Ex: 0.5 for 50% */
  initialWidth?: number;
};

@Component({
  selector: 'cipo-horizontal-splitter',
  imports: [FlexLayoutModule, MatIconModule],
  templateUrl: './horizontal-splitter.component.html',
  styleUrl: './horizontal-splitter.component.scss',
})
export class HorizontalSplitterComponent implements AfterViewInit, OnDestroy {
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);
  private resizeObserver: ResizeObserver;

  /** Options for left pane. Right pane will have complementary options */
  leftPaneOptions = input<PaneOptions>({
    minWidth: 0.25,
    maxWidth: 0.75,
    initialWidth: 0.5,
  });
  /** Step size for resizing in percents. Based on this value, the onResize event will be emitted
   * when the pane is resized by this amount. Default is 10 (10%).
   * @default 10
   */
  resizeStepper = input<number>(10);
  /** Event emitted when the pane is resized. The value is the new size of the left pane in percent. */
  onResize = output<number>();

  resizePercent = linkedSignal(() => (this.leftPaneOptions().initialWidth || 0.5) * 100);
  showPane = computed(() => {
    const leftPaneOptions = this.leftPaneOptions();
    return {
      left: leftPaneOptions.initialWidth < 1,
      right: leftPaneOptions.initialWidth > 0,
      both: leftPaneOptions.initialWidth > 0 && leftPaneOptions.initialWidth < 1,
    };
  });
  roundedResizePercent = computed(() => Math.round(this.resizePercent() / this.resizeStepper()) * this.resizeStepper());

  private resizingState = {
    isResizing: false,
    leftPane: null as HTMLElement | null,
    rect: null as DOMRect | null,
  };

  constructor() {
    /* Each time the leftPaneOptions changes, set the left pane width accordingly */
    effect(() => this.setPaneWidth(this.leftPaneOptions().initialWidth));

    /* Each time the rounded resize percent changes, emit the onResize event (by default 10%) */
    effect(() => this.onResize.emit(this.roundedResizePercent()));
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.setPaneWidth(this.resizePercent() / 100));
    this.resizeObserver.observe(this.el.nativeElement.querySelector('.resize-container'));
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  private setPaneWidth(leftPanePercent: number) {
    const { leftPane, rect } = this.getElements();
    if (!leftPane || !rect) {
      return;
    }
    const newLeftWidth = rect.width * leftPanePercent;
    this.renderer.setStyle(leftPane, 'width', `${newLeftWidth}px`);
  }

  @HostListener('document:mousemove', ['$event']) onMouseMove(event: MouseEvent) {
    const { isResizing, leftPane, rect } = this.resizingState;
    if (!isResizing) {
      return;
    }
    const { minWidth, maxWidth } = this.leftPaneOptions();
    const leftPaneMinWidth = rect.width * minWidth!;
    const rightPaneMinWidth = rect.width * (1 - maxWidth);
    let newLeftWidth = event.clientX - rect.left;

    // Ensure the panes do not shrink below the minimum width
    newLeftWidth = Math.max(leftPaneMinWidth, Math.min(newLeftWidth, rect.width - rightPaneMinWidth));
    this.renderer.setStyle(leftPane, 'width', `${newLeftWidth}px`);

    /* Save the integer percent of the resizer. Ex: '38' means '38% for the left pane and 62% for the right pane' */
    this.resizePercent.set(Math.round((newLeftWidth / rect.width) * 100));
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.resizingState = {
      isResizing: false,
      leftPane: null,
      rect: null,
    };
  }

  startResizing() {
    this.resizingState = {
      ...this.getElements(),
      isResizing: true,
    };
  }

  private getElements() {
    return {
      leftPane: this.el.nativeElement.querySelector('[leftContent]'),
      rect: this.el.nativeElement.querySelector('.resize-container').getBoundingClientRect(),
    };
  }
}
